import { createObservableOperation } from "owa-observable-operation";
import { getDateRangeISOString } from "hybridspace-common/lib/utils/getDateRangeISOString";
import type { DateRange } from "owa-datetime-utils";
import fetchFWHForWeek from "../services/fetchFWHForWeek";
import type { WorkLocationSurfaceType } from "owa-calendar-working-hours-location-utils";
import type { OwaDate } from "owa-datetime";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import type { WorkLocationType, DayOfWeek, LocationIdType, LocationEntityType, LocationSourceType, FlexibleWorkingHoursView, Location, ExtendedDateTime, Maybe, ListFlexibleWorkingHoursResponse, } from "owa-graph-schema";

const dest = createObservableOperation("PLACES_LOAD_FWH", (dateRange: DateRange): {
    dateRange: DateRange;
} => ({ dateRange }), ({ dateRange }) => getDateRangeISOString(dateRange), ({ dateRange }) => fetchFWHForWeek(dateRange), () => getModuleContextMailboxInfo());
const loadFWHSucceeded: (actionMessage: {
    dateRange: DateRange;
} & {
    result: {
        summaries: never[];
        recurringFlexibleWorkingHours: {
            __typename?: "ListFlexibleWorkingHoursResponse";
            segments?: Array<{
                __typename?: "FlexibleWorkingHours";
                id: string;
                workLocationType: WorkLocationType;
                start: {
                    __typename?: "ExtendedDateTime";
                    dateTime: string;
                    timeZone: {
                        __typename?: "TimeZoneWH";
                        name: string;
                    };
                };
                end: {
                    __typename?: "ExtendedDateTime";
                    dateTime: string;
                    timeZone: {
                        __typename?: "TimeZoneWH";
                        name: string;
                    };
                };
                recurrenceDetailsWH?: {
                    __typename?: "RecurrenceDetailsWH";
                    recurrence: {
                        __typename?: "PatternedRecurrenceWH";
                        recurrencePattern?: {
                            __typename?: "RecurrencePatternWH";
                            weeklyRecurrencePattern: {
                                __typename?: "WeeklyRecurrencePatternWH";
                                interval: number;
                                firstDayOfWeek: DayOfWeek;
                                daysOfWeek: Array<DayOfWeek>;
                            };
                        } | null;
                        recurrenceRange?: {
                            __typename?: "RecurrenceRangeWH";
                            endDateRecurrenceRange?: {
                                __typename?: "EndDateRecurrenceRangeWH";
                                start?: {
                                    __typename?: "ExtendedDateTime";
                                    dateTime: string;
                                    timeZone: {
                                        __typename?: "TimeZoneWH";
                                        name: string;
                                    };
                                } | null;
                                end?: {
                                    __typename?: "ExtendedDateTime";
                                    dateTime: string;
                                    timeZone: {
                                        __typename?: "TimeZoneWH";
                                        name: string;
                                    };
                                } | null;
                            } | null;
                            noEndRecurrenceRange?: {
                                __typename?: "NoEndRecurrenceRangeWH";
                                start?: {
                                    __typename?: "ExtendedDateTime";
                                    dateTime: string;
                                    timeZone: {
                                        __typename?: "TimeZoneWH";
                                        name: string;
                                    };
                                } | null;
                            } | null;
                        } | null;
                    };
                    timeZone: {
                        __typename?: "TimeZoneWH";
                        name: string;
                    };
                } | null;
                workLocationDetails?: {
                    __typename?: "Location";
                    id?: string | null;
                    idType?: LocationIdType | null;
                    displayName?: string | null;
                    annotation?: string | null;
                    locationType?: LocationEntityType | null;
                    locationUri?: string | null;
                    locationSource?: LocationSourceType | null;
                } | null;
            }> | null;
        };
        occurringFlexibleWorkingHours: FlexibleWorkingHoursView[];
    } | {
        summaries: {
            date: OwaDate;
            timestamp: number;
            workLocationSurfaceType: WorkLocationSurfaceType;
            buildingId?: Location["id"];
            buildingIds: Map<Location["id"], Location["displayName"]>;
            segments: {
                start: ExtendedDateTime;
                end: ExtendedDateTime;
                workLocationType: WorkLocationType;
                workLocationDetails?: Maybe<Location> | undefined;
            }[];
        }[];
        recurringFlexibleWorkingHours: ListFlexibleWorkingHoursResponse;
        occurringFlexibleWorkingHours: FlexibleWorkingHoursView[];
    };
}) => {
    dateRange: DateRange;
} & {
    result: {
        summaries: never[];
        recurringFlexibleWorkingHours: {
            __typename?: "ListFlexibleWorkingHoursResponse";
            segments?: Array<{
                __typename?: "FlexibleWorkingHours";
                id: string;
                workLocationType: WorkLocationType;
                start: {
                    __typename?: "ExtendedDateTime";
                    dateTime: string;
                    timeZone: {
                        __typename?: "TimeZoneWH";
                        name: string;
                    };
                };
                end: {
                    __typename?: "ExtendedDateTime";
                    dateTime: string;
                    timeZone: {
                        __typename?: "TimeZoneWH";
                        name: string;
                    };
                };
                recurrenceDetailsWH?: {
                    __typename?: "RecurrenceDetailsWH";
                    recurrence: {
                        __typename?: "PatternedRecurrenceWH";
                        recurrencePattern?: {
                            __typename?: "RecurrencePatternWH";
                            weeklyRecurrencePattern: {
                                __typename?: "WeeklyRecurrencePatternWH";
                                interval: number;
                                firstDayOfWeek: DayOfWeek;
                                daysOfWeek: Array<DayOfWeek>;
                            };
                        } | null;
                        recurrenceRange?: {
                            __typename?: "RecurrenceRangeWH";
                            endDateRecurrenceRange?: {
                                __typename?: "EndDateRecurrenceRangeWH";
                                start?: {
                                    __typename?: "ExtendedDateTime";
                                    dateTime: string;
                                    timeZone: {
                                        __typename?: "TimeZoneWH";
                                        name: string;
                                    };
                                } | null;
                                end?: {
                                    __typename?: "ExtendedDateTime";
                                    dateTime: string;
                                    timeZone: {
                                        __typename?: "TimeZoneWH";
                                        name: string;
                                    };
                                } | null;
                            } | null;
                            noEndRecurrenceRange?: {
                                __typename?: "NoEndRecurrenceRangeWH";
                                start?: {
                                    __typename?: "ExtendedDateTime";
                                    dateTime: string;
                                    timeZone: {
                                        __typename?: "TimeZoneWH";
                                        name: string;
                                    };
                                } | null;
                            } | null;
                        } | null;
                    };
                    timeZone: {
                        __typename?: "TimeZoneWH";
                        name: string;
                    };
                } | null;
                workLocationDetails?: {
                    __typename?: "Location";
                    id?: string | null;
                    idType?: LocationIdType | null;
                    displayName?: string | null;
                    annotation?: string | null;
                    locationType?: LocationEntityType | null;
                    locationUri?: string | null;
                    locationSource?: LocationSourceType | null;
                } | null;
            }> | null;
        };
        occurringFlexibleWorkingHours: FlexibleWorkingHoursView[];
    } | {
        summaries: {
            date: OwaDate;
            timestamp: number;
            workLocationSurfaceType: WorkLocationSurfaceType;
            buildingId?: Location["id"];
            buildingIds: Map<Location["id"], Location["displayName"]>;
            segments: {
                start: ExtendedDateTime;
                end: ExtendedDateTime;
                workLocationType: WorkLocationType;
                workLocationDetails?: Maybe<Location> | undefined;
            }[];
        }[];
        recurringFlexibleWorkingHours: ListFlexibleWorkingHoursResponse;
        occurringFlexibleWorkingHours: FlexibleWorkingHoursView[];
    };
} = dest.onSucceeded;
const loadFWHAsync: (dateRange: DateRange) => Promise<{
    summaries: never[];
    recurringFlexibleWorkingHours: {
        __typename?: "ListFlexibleWorkingHoursResponse";
        segments?: Array<{
            __typename?: "FlexibleWorkingHours";
            id: string;
            workLocationType: WorkLocationType;
            start: {
                __typename?: "ExtendedDateTime";
                dateTime: string;
                timeZone: {
                    __typename?: "TimeZoneWH";
                    name: string;
                };
            };
            end: {
                __typename?: "ExtendedDateTime";
                dateTime: string;
                timeZone: {
                    __typename?: "TimeZoneWH";
                    name: string;
                };
            };
            recurrenceDetailsWH?: {
                __typename?: "RecurrenceDetailsWH";
                recurrence: {
                    __typename?: "PatternedRecurrenceWH";
                    recurrencePattern?: {
                        __typename?: "RecurrencePatternWH";
                        weeklyRecurrencePattern: {
                            __typename?: "WeeklyRecurrencePatternWH";
                            interval: number;
                            firstDayOfWeek: DayOfWeek;
                            daysOfWeek: Array<DayOfWeek>;
                        };
                    } | null;
                    recurrenceRange?: {
                        __typename?: "RecurrenceRangeWH";
                        endDateRecurrenceRange?: {
                            __typename?: "EndDateRecurrenceRangeWH";
                            start?: {
                                __typename?: "ExtendedDateTime";
                                dateTime: string;
                                timeZone: {
                                    __typename?: "TimeZoneWH";
                                    name: string;
                                };
                            } | null;
                            end?: {
                                __typename?: "ExtendedDateTime";
                                dateTime: string;
                                timeZone: {
                                    __typename?: "TimeZoneWH";
                                    name: string;
                                };
                            } | null;
                        } | null;
                        noEndRecurrenceRange?: {
                            __typename?: "NoEndRecurrenceRangeWH";
                            start?: {
                                __typename?: "ExtendedDateTime";
                                dateTime: string;
                                timeZone: {
                                    __typename?: "TimeZoneWH";
                                    name: string;
                                };
                            } | null;
                        } | null;
                    } | null;
                };
                timeZone: {
                    __typename?: "TimeZoneWH";
                    name: string;
                };
            } | null;
            workLocationDetails?: {
                __typename?: "Location";
                id?: string | null;
                idType?: LocationIdType | null;
                displayName?: string | null;
                annotation?: string | null;
                locationType?: LocationEntityType | null;
                locationUri?: string | null;
                locationSource?: LocationSourceType | null;
            } | null;
        }> | null;
    };
    occurringFlexibleWorkingHours: FlexibleWorkingHoursView[];
} | {
    summaries: {
        date: OwaDate;
        timestamp: number;
        workLocationSurfaceType: WorkLocationSurfaceType;
        buildingId?: Location["id"];
        buildingIds: Map<Location["id"], Location["displayName"]>;
        segments: {
            start: ExtendedDateTime;
            end: ExtendedDateTime;
            workLocationType: WorkLocationType;
            workLocationDetails?: Maybe<Location> | undefined;
        }[];
    }[];
    recurringFlexibleWorkingHours: ListFlexibleWorkingHoursResponse;
    occurringFlexibleWorkingHours: FlexibleWorkingHoursView[];
}> = dest.startAsync;
const loadFWH: (dateRange: DateRange) => {
    dateRange: DateRange;
} = dest.start;

export { loadFWHSucceeded, loadFWHAsync, loadFWH };
